<template>
  <div class="ledger-report">
    <!-- Control Buttons -->
    <div class="no-print control-bar">
      <h4 class="mb-0">Student Ledger Report</h4>
      <div>
        <b-button variant="primary" @click="print" size="sm">
          <feather-icon icon="PrinterIcon" size="16" class="mr-50" />
          Print
        </b-button>
        <b-button
          variant="outline-secondary"
          @click="close"
          size="sm"
          class="ml-2"
        >
          <feather-icon icon="XIcon" size="16" class="mr-50" />
          Close
        </b-button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 2.5rem; height: 2.5rem" />
      <p class="mt-3 font-weight-medium">{{ loadingText }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h5 class="text-danger font-weight-medium">{{ error }}</h5>
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Header -->
      <div class="page-header">
        <h3 class="school-name">Student Ledger</h3>
        <div class="header-meta mt-2">
          <span
            ><strong>Student:</strong> {{ studentName }} ({{ studentId }})</span
          >
          <span class="separator">|</span>
          <span><strong>Date:</strong> {{ currentDateTime }}</span>
        </div>
      </div>

      <!-- Ledger Table -->
      <table v-if="ledgerData.length > 0" class="ledger-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Receipt Ref</th>
            <th>Description</th>
            <th class="text-right">Debit</th>
            <th class="text-right">Credit</th>
            <th class="text-right">Balance</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in ledgerData" :key="index">
            <td>{{ formatDate(item.date || item.Date) }}</td>
            <td>{{ item.receiptRef || item.ReceiptRef || "-" }}</td>
            <td>
              {{
                item.description ||
                item.Description ||
                item.particulars ||
                item.Particulars
              }}
            </td>
            <td class="text-right">
              {{ formatCurrency(item.debit || item.Debit || 0) }}
            </td>
            <td class="text-right">
              {{ formatCurrency(item.credit || item.Credit || 0) }}
            </td>
            <td class="text-right font-weight-bold">
              {{ formatCurrency(item.balance || item.Balance || 0) }}
            </td>
          </tr>
        </tbody>
      </table>

      <!-- No Data -->
      <div v-else class="text-center py-5">
        <h6 class="text-muted">No ledger data available</h6>
      </div>

      <!-- Footer -->
      <div class="page-footer">
        <small><strong>Generated:</strong> {{ currentDateTime }}</small>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "LedgerPrint",
  components: {
    BButton,
    BSpinner,
  },
  data() {
    return {
      loading: false,
      loadingText: "Loading ledger data...",
      error: null,
      ledgerData: [],
      studentId: "",
      studentName: "",
    };
  },
  computed: {
    currentDateTime() {
      const now = new Date();
      return now.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  created() {
    this.loadReportData();
  },
  methods: {
    async loadReportData() {
      try {
        this.loading = true;
        this.error = null;

        const { db, sID, studentName } = this.$route.query;
        this.studentId = sID;
        this.studentName = studentName || "N/A";

        const url = `https://apiv26.myskool.app/api/Fees/CreateStudentsLedger`;

        const response = await axios.get(url, {
          params: {
            db: db,
            sID: sID,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (response.data) {
          // Handle response structure - assuming standard array or nested data
          let data = response.data;
          if (data.data) data = data.data;

          if (Array.isArray(data)) {
            this.ledgerData = data;
          } else {
            // Provide fallback if data structure is unexpected
            this.ledgerData = [];
            console.warn("Unexpected data format:", response.data);
          }
        } else {
          this.ledgerData = [];
        }
      } catch (error) {
        console.error("Failed to load ledger data:", error);
        this.error =
          error.response?.data?.message ||
          "Failed to load ledger report. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      try {
        return new Date(dateString).toLocaleDateString("en-GB");
      } catch (e) {
        return dateString;
      }
    },

    formatCurrency(value) {
      if (value === undefined || value === null) return "-";
      return Number(value).toFixed(2);
    },

    print() {
      window.print();
    },

    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
* {
  font-weight: 500;
}

.ledger-report {
  background: #f0f0f0;
  min-height: 100vh;
  padding: 15px;
}

.control-bar {
  background: white;
  padding: 12px 20px;
  margin-bottom: 15px;
  border: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h4 {
    color: #000;
    font-weight: 600;
  }
}

.report-page {
  background: white;
  padding: 20px;
  border: 1px solid #ccc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 1000px;
  margin: 0 auto;
}

.page-header {
  text-align: center;
  border-bottom: 2px solid #000;
  padding-bottom: 12px;
  margin-bottom: 15px;

  .school-name {
    font-size: 1.4rem;
    font-weight: 700;
    color: #000;
    margin-bottom: 5px;
  }

  .header-meta {
    font-size: 0.9rem;
    color: #000;
    font-weight: 500;

    .separator {
      margin: 0 10px;
      color: #999;
    }
  }
}

.ledger-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 15px;
  font-size: 0.9rem;

  thead {
    background: #f5f5f5;

    th {
      padding: 10px;
      border: 1px solid #ddd;
      color: #000;
      font-weight: 700;
      text-align: left;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #eee;

      &:nth-child(even) {
        background-color: #f9f9f9;
      }

      td {
        padding: 8px 10px;
        color: #333;
        border: 1px solid #ddd;
      }
    }
  }
}

.page-footer {
  text-align: center;
  padding-top: 12px;
  margin-top: 15px;
  border-top: 1px solid #ccc;
  color: #666;
}

@media print {
  .no-print {
    display: none !important;
  }

  .ledger-report {
    background: white;
    padding: 0;
    min-height: auto;
  }

  .report-page {
    box-shadow: none;
    border: none;
    padding: 0;
    margin: 0;
    max-width: 100%;
  }

  .ledger-table {
    th,
    td {
      border: 1px solid #000;
    }
  }
}

.text-right {
  text-align: right;
}
</style>
